<template>
  <div class="flex h-full w-full flex-col justify-center">
    <slot />
    <loader :is-loading="isLoading" />
    <div
      v-if="!isLoading"
      ref="pdfDiv"
      class="h-full max-h-[1200px] w-full overflow-x-auto overflow-y-auto border"
      :contenteditable="true"
      @keydown.ctrl.exact="handleShortcuts"
      @keydown.meta.exact="handleShortcuts"
      @keydown.exact.prevent
      @mousemove="cacheMousePosition"
    >
      <vue-pdf-embed
        :id="`vue-pdf-embed-${fileId}`"
        class="group relative w-full"
        :source="file.url"
        :contenteditable="false"
        @rendered="getScaleFactor"
      >
        <template v-if="showEditOverlay" #after-page="{ page }">
          <div
            class="edit-overlay group absolute right-0 left-0 z-20"
            :style="getStyleForPage(page)"
            :data-page="page"
            @drop.prevent="handleFieldDrop($event, page)"
            @dragenter.prevent
            @dragover.prevent
            @click.stop="clearFieldIfNeeded"
          >
            <template v-if="activeRoleFieldsWithDetails?.length">
              <template
                v-for="role in activeRoleFieldsWithDetails"
                :key="role.id"
              >
                <div
                  v-for="field in role.fields"
                  v-show="page === field.pageNumber"
                  :key="field.id"
                  class="field no-drop no-clear absolute flex h-8 items-center space-x-2 rounded-sm border border-icon-user-purple bg-violet-50 px-2 leading-7 text-headline"
                  :class="[
                    field.role.role,
                    `${field.role.role}-${getCountOfRole(role, roles)}`,
                    { 'missing-context': isMissingContext(field) },
                  ]"
                  :data-role="field.role.role"
                  :data-role-id="field.role.id"
                  :data-tab-label="field.tabLabel"
                  :data-required="field.required"
                  :data-index="getCountOfRole(role, roles)"
                  :style="getStyleForField(field)"
                  draggable="true"
                  @dragstart="startDrag($event, field)"
                  @dragend="stopDrag"
                >
                  <icon-base
                    :icon="IconDragHandle"
                    view-box="0 0 12 20"
                    height="12"
                    width="7"
                    class="no-drop cursor-grab"
                    @click.stop
                  />
                  <div
                    class="no-drop flex items-center"
                    @click.stop="$emit('field:edit', field)"
                  >
                    <icon-base
                      v-if="field.tabLabel === 'checkbox'"
                      :icon="IconCheck"
                      class="no-drop mx-1 text-headline"
                    />
                    <template v-else>
                      {{ field.context?.value.name ?? field.tabLabel }}
                    </template>
                    <span v-if="field.required" class="no-drop text-error">
                      *
                    </span>
                  </div>
                </div>
              </template>
            </template>
          </div>
        </template>
      </vue-pdf-embed>
      <span v-if="errorOccured">
        {{ $t("COMMON.ERROR_OCCURRED") }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { type PropType, computed, onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import VuePdfEmbed from "vue-pdf-embed";

import { storeToRefs } from "pinia";

import { CUSTOM_ATTRIBUTE } from "@/helpers/constants/contractBuilder";
import { getCountOfRole } from "@/helpers/contractBuilder";
import { makeRandomString } from "@/helpers/formatting";
import { usePromiseWrapper } from "@/hooks/common";
import { useContractBuilder } from "@/hooks/contractBuilder";
import { useNotification } from "@/hooks/notifications";
import { axiosClient } from "@/services/client";
import { useContractBuilderStore } from "@/stores/contractBuilder";

import IconCheck from "@/components/icons/IconCheck.vue";
import IconDragHandle from "@/components/icons/IconDragHandle.vue";

import type {
  ContractField,
  ContractFieldWithName,
  RoleAndFieldsDetails,
  RoleWithID,
} from "@/models/contractBuilder";

type ContractFieldCopyValues = Pick<
  ContractFieldWithName,
  "tabLabel" | "required" | "role"
>;

const emit = defineEmits<{
  "field:edit": [ContractFieldWithName];
  "field:clear": [void];
}>();

const props = defineProps({
  fileUrl: {
    type: String,
    required: true,
  },
  fileId: {
    type: [String, Number],
    required: false,
  },
  role: {
    type: Object as PropType<RoleWithID>,
    required: true,
  },
  isEditing: {
    type: Boolean,
    required: false,
    default: false,
  },
  roles: {
    type: Array as PropType<RoleWithID[]>,
    required: true,
  },
});

const store = useContractBuilderStore();
const { activeTemplate } = storeToRefs(store);
const { contractTemplatesOptions } = useContractBuilder();
const { showMessage } = useNotification();
const { t } = useI18n();

const file = ref({
  mimeType: "",
  url: "",
});
const errorOccured = ref(false);
const scaleFactor = ref(1);
const pageHeight = ref(0);
const showEditOverlay = ref(false);
const isDragging = ref(false);
const pdfDiv = ref<HTMLDivElement | null>(null);
const mousePosition = ref({ x: 0, y: 0 });
const copiedField = ref<ContractFieldCopyValues | null>(null);

const activeRoleFields = computed(() => {
  return activeTemplate.value?.content?.recipients;
});

const { fetchWrapper: getFile, loading: isLoading } = usePromiseWrapper(
  async () => {
    const response = await axiosClient.get(
      props.fileUrl.replace("http:", "https:"),
      {
        baseURL: "",
        responseType: "blob",
      }
    );
    const mimeType = response.headers["content-type"];
    const url = URL.createObjectURL(response.data);
    file.value = {
      url,
      mimeType,
    };
  },
  {
    onError: () => {
      errorOccured.value = true;
      file.value = {
        mimeType: "",
        url: "",
      };
    },
  }
);

const handleFieldDrop = (event: DragEvent, page: number) => {
  const jsonData = event.dataTransfer?.getData("text");
  if (
    !jsonData ||
    (event.target as HTMLElement)?.classList?.contains("no-drop")
    // firefox is fine with turning off pointer events but chrome
    // immediately disables dragging so we need to check for the class
    // so the x/y position is not incorrectly assigned
  ) {
    return;
  }
  const data = JSON.parse(jsonData) as ContractFieldWithName & {
    cursorOffsetX: number;
    cursorOffsetY: number;
    tab_label?: string;
  };
  const target = event.target as HTMLElement;
  const rect = target.getBoundingClientRect();
  // we must account for the scale factor to get the correct position
  // this may need adjusted based on real world testing
  // but it works locally in keeping the fields in the correct position
  // regardless of the viewport (and PDF rendering) size
  const offsetX =
    (event.clientX - rect.left - (data.cursorOffsetX || 0)) / scaleFactor.value;
  const offsetY =
    (event.clientY - rect.top - (data.cursorOffsetY || 0)) / scaleFactor.value;
  const payload = {
    role: data.role,
    name: data.context?.value.name ?? data.tabLabel ?? data.tab_label,
    id: data.id ?? makeRandomString(),
    tabLabel: data.tabLabel ?? data.tab_label,
    context: data.context ?? undefined,
    pageNumber: page,
    xPosition: Math.round(offsetX),
    yPosition: Math.round(offsetY),
    required: Boolean(data.required),
  };
  store.updateContractTemplateFields(payload);
  if (props.isEditing) {
    emit("field:edit", payload);
  }
};

const getScaleFactor = () => {
  const pdfEmbed = document.getElementById(`vue-pdf-embed-${props.fileId}-1`);
  pageHeight.value = pdfEmbed?.clientHeight ?? 0;
  const style = pdfEmbed?.style;

  scaleFactor.value = style?.getPropertyValue("--scale-factor")
    ? Number(style?.getPropertyValue("--scale-factor"))
    : 1;
  showEditOverlay.value = true;
};

const isMissingContext = (field: ContractField) => {
  if (field.tabLabel !== CUSTOM_ATTRIBUTE) {
    return false;
  }
  return !field.context?.value;
};

const clearFieldIfNeeded = (e: MouseEvent) => {
  if (e.target && (e.target as HTMLElement)?.classList.contains("no-clear")) {
    return;
  }
  emit("field:clear");
};

const activeRoleFieldsWithDetails = computed<
  RoleAndFieldsDetails[] | undefined
>(() => {
  return activeRoleFields.value?.map((role) => {
    return {
      ...role,
      fields: role.fields.map((field) => {
        return {
          ...field,
          name: contractTemplatesOptions.value?.fields
            .flatMap((category) => category.fields)
            .find((templateField) => {
              if (
                templateField.tab_label === CUSTOM_ATTRIBUTE &&
                templateField.context?.value
              ) {
                return (
                  templateField.context.value.id === field.context?.value.id
                );
              }
              return templateField.tab_label === field.tabLabel;
            })?.name,
          role: role,
        };
      }),
    };
  });
});

const getStyleForPage = (page: number) => {
  const style = {
    top: `${pageHeight.value * (page - 1)}px`,
    height: `${pageHeight.value}px`,
  };
  return style;
};

const getStyleForField = (field: ContractField) => {
  const style = {
    top: `${Number(field.yPosition) * scaleFactor.value}px`,
    left: `${Number(field.xPosition) * scaleFactor.value}px`,
  };
  return style;
};

const startDrag = (e: DragEvent, field: ContractFieldWithName) => {
  if (!e.dataTransfer) {
    return;
  }
  // get the offset of the mouse cursor so the drop adheres to the bounding box of the target element
  const target = e.target as HTMLElement;
  const rect = target.getBoundingClientRect();
  const cursorOffsetX = e.clientX - rect.left;
  const cursorOffsetY = e.clientY - rect.top;
  e.dataTransfer.setData(
    "text",
    JSON.stringify({ ...field, cursorOffsetX, cursorOffsetY })
  );
  isDragging.value = true;
};

const stopDrag = () => {
  isDragging.value = false;
};

const handleShortcuts = (event: KeyboardEvent) => {
  const ALLOWED_SHORTCUT_KEYS = ["c", "v"];
  const shortcutKey = event.key.toLocaleLowerCase();
  const keyHandlers = {
    c: copyField,
    v: pasteField,
  };
  if (
    !ALLOWED_SHORTCUT_KEYS.includes(event.key) ||
    !(shortcutKey in keyHandlers)
  ) {
    return;
  }
  event.preventDefault();
  keyHandlers[shortcutKey as keyof typeof keyHandlers]();
};

const cacheMousePosition = (event: MouseEvent) => {
  // cache mouse position for copy/paste
  mousePosition.value = {
    x: Math.round(event.clientX),
    y: Math.round(event.clientY),
  };
};

const copyField = async () => {
  const { x, y } = mousePosition.value;
  const element = document.elementFromPoint(x, y);
  // get parent elements up to one level to see if it's a field
  // if it is, get the field data
  if (element?.classList.contains("field")) {
    copiedField.value = getCopiedFieldData(element as HTMLElement);
    showMessage(t("COMMON.COPIED"), "success");
    return;
  }
  if (element?.parentElement?.classList.contains("field")) {
    copiedField.value = getCopiedFieldData(element.parentElement);
    showMessage(t("COMMON.COPIED"), "success");
    return;
  }
  showMessage(t("CONTRACTS.CONTRACT_BUILDER.NOTHING_TO_COPY"), "error");
};

const getCopiedFieldData = (element: HTMLElement) => {
  const roleName = element.dataset.role || "";
  const roleId = element.dataset.roleId || "";
  const tabLabel = element.dataset.tabLabel || "";
  const required = element.dataset.required === "true";
  return {
    role: {
      role: roleName as RoleWithID["role"],
      id: roleId,
    },
    tabLabel,
    required,
  };
};

const pasteField = () => {
  if (!copiedField.value) {
    showMessage(t("CONTRACTS.CONTRACT_BUILDER.NOTHING_TO_PASTE"), "error");
    return;
  }
  const { x, y } = mousePosition.value;
  const parentOffset = pdfDiv.value?.getBoundingClientRect();
  if (!parentOffset) {
    return;
  }
  const element = document.elementFromPoint(x, y);
  if (!element?.classList.contains("edit-overlay")) {
    showMessage(t("CONTRACTS.CONTRACT_BUILDER.CANNOT_PASTE_HERE"), "error");
    return;
  }
  const pageX = (x - parentOffset.left) / scaleFactor.value;
  const pageY = (y - parentOffset.top) / scaleFactor.value;
  const pageNumber = Number((element as HTMLElement).dataset.page);
  const payload = {
    role: copiedField.value.role,
    tabLabel: copiedField.value.tabLabel,
    required: copiedField.value.required,
    id: makeRandomString(),
    pageNumber,
    xPosition: Math.round(pageX),
    yPosition: Math.round(pageY),
  };
  store.updateContractTemplateFields(payload);
  copiedField.value = null;
  if (props.isEditing) {
    emit("field:edit", payload);
  }
};

onMounted(getFile);

onUnmounted(() => {
  if (file.value.url) {
    URL.revokeObjectURL(file.value.url);
  }
  activeTemplate.value = null;
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.field::before {
  content: attr(data-role) " " attr(data-index);
  @apply absolute -top-5 -left-0-25 z-10 hidden min-w-20 rounded-xs py-1 text-center text-xxs leading-none font-semibold text-white uppercase;
}

.field.signer,
.field.signer-1 {
  @apply border-secondary-purple-1 bg-secondary-purple-1/50;
  &::before {
    @apply bg-secondary-purple-1;
  }
}

.field.signer-2 {
  @apply border-secondary-yellow bg-secondary-yellow/50;
  &::before {
    @apply bg-secondary-yellow;
  }
}

.field.signer-3 {
  @apply border-flush-orange bg-flush-orange/50;
  &::before {
    @apply bg-flush-orange;
  }
}

.field.signer-4 {
  @apply border-error bg-error/50;
  &::before {
    @apply bg-error;
  }
}

.field.guarantor,
.field.guarantor-1 {
  @apply border-secondary-blue-4 bg-secondary-blue-4/50;
  &&::before {
    @apply bg-secondary-blue-4;
  }
}

.field.guarantor-2 {
  @apply border-lime-400 bg-lime-400/50;
  &&::before {
    @apply bg-lime-400;
  }
}

.field.guarantor-3 {
  @apply border-fuchsia-300 bg-fuchsia-300/50;
  &&::before {
    @apply bg-fuchsia-300;
  }
}

.field.guarantor-4 {
  @apply border-amber-800 bg-amber-800/50;
  &&::before {
    @apply bg-amber-800;
  }
}

.field.client,
.field.client-1 {
  @apply border-success bg-success/50;
  &&::before {
    @apply bg-success;
  }
}

.field.client-2 {
  @apply border-stone-400 bg-stone-400/50;
  &&::before {
    @apply bg-stone-400;
  }
}

.field.client-3 {
  @apply border-green-700 bg-green-700/50;
  &&::before {
    @apply bg-green-700;
  }
}

.field.client-4 {
  @apply border-sky-900 bg-sky-900/50;
  &&::before {
    @apply bg-sky-900;
  }
}
.field:hover::before {
  @apply block;
}

.field.missing-context {
  @apply border-2 border-error;
}
</style>
